import { existsSync, readdirSync, readFileSync, statSync } from 'fs'
import { join, relative, sep, basename } from 'path'
import { parseExports } from './parse-exports.js'

export interface InferredInterface {
  module: string
  source_glob: string
  exports: string[]
}

/**
 * Infer the project's module→export mapping.
 *
 * Priority: TypeScript first (flat src/ tree), then Maven/Java multi-module
 * (pom.xml <modules> → src/main/java), then a structured scan of the project
 * root for common source layouts.
 *
 * Returns empty array only when no source files are found in any recognized
 * layout — never silently returns empty for a real project.
 */
export async function inferProjectGraph(projectRoot: string): Promise<InferredInterface[]> {
  if (!existsSync(projectRoot)) {
    throw new Error(`Project root not found: ${projectRoot}`)
  }

  // 1. Try TypeScript (flat src/ with .ts files)
  const tsSrcDir = join(projectRoot, 'src')
  if (existsSync(tsSrcDir)) {
    const tsFiles = collectTsFiles(tsSrcDir)
    if (tsFiles.length > 0) {
      return await inferTsGraph(projectRoot, tsFiles)
    }
  }

  // 2. Try Maven multi-module Java (pom.xml with <modules>)
  const pomPath = join(projectRoot, 'pom.xml')
  if (existsSync(pomPath)) {
    const javaGraph = await inferMavenJavaGraph(projectRoot, pomPath)
    if (javaGraph.length > 0) return javaGraph
  }

  // 3. Generic recursive Java scan (no pom.xml, but .java files exist somewhere)
  const javaFiles = collectJavaFilesRecursive(projectRoot)
  if (javaFiles.length > 0) {
    return await inferJavaGraphFromFiles(projectRoot, javaFiles)
  }

  return []
}

// ─── TypeScript pipeline ───────────────────────────────────────────────

async function inferTsGraph(
  projectRoot: string,
  files: string[],
): Promise<InferredInterface[]> {
  const byModule = new Map<string, InferredInterface>()

  for (const absPath of files) {
    const rel = relative(projectRoot, absPath).replace(/\\/g, '/')
    if (!rel.startsWith('src/')) continue

    let content: string
    try {
      content = readFileSync(absPath, 'utf-8')
    } catch {
      continue
    }
    if (!content.trim()) continue

    const symbols = await parseExports(content, rel)
    const moduleName = deriveModuleName(rel)

    const existing = byModule.get(moduleName)
    const names = symbols.map(s => s.name)
    if (existing) {
      existing.exports.push(...names)
    } else {
      byModule.set(moduleName, {
        module: moduleName,
        source_glob: `src/${moduleName}/**/*.ts`,
        exports: [...names],
      })
    }
  }

  for (const iface of byModule.values()) {
    iface.exports = [...new Set(iface.exports)].sort()
  }

  return [...byModule.values()].sort((a, b) => a.module.localeCompare(b.module))
}

function collectTsFiles(srcDir: string): string[] {
  const out: string[] = []
  const walk = (dir: string) => {
    let entries: string[]
    try {
      entries = readdirSync(dir)
    } catch {
      return
    }
    for (const name of entries) {
      if (name === 'node_modules') continue
      const full = join(dir, name)
      let st
      try {
        st = statSync(full)
      } catch {
        continue
      }
      if (st.isDirectory()) {
        walk(full)
      } else if (st.isFile() && full.endsWith('.ts') && !full.endsWith('.d.ts')) {
        out.push(full)
      }
    }
  }
  walk(srcDir)
  return out
}

// ─── Maven / Java pipeline ─────────────────────────────────────────────

/**
 * Parse `<module>` entries from a Maven pom.xml.
 * Returns module directory names relative to project root.
 */
function parseMavenModules(pomPath: string): string[] {
  let content: string
  try {
    content = readFileSync(pomPath, 'utf-8')
  } catch {
    return []
  }

  const modules: string[] = []
  // Match <module>name</module> — supports whitespace and optional <modules> wrapper
  const re = /<module>\s*([^<\s]+)\s*<\/module>/gi
  let match: RegExpExecArray | null
  while ((match = re.exec(content)) !== null) {
    modules.push(match[1])
  }
  return modules
}

/**
 * Build the graph from a Maven multi-module project.
 *
 * 1. Parse pom.xml for <module> entries.
 * 2. For each module, scan {module}/src/main/java/ for .java files.
 * 3. Extract public interface/class/enum/record declarations via regex.
 * 4. If no <module> entries, fall back to recursive .java scan from root.
 */
async function inferMavenJavaGraph(
  projectRoot: string,
  pomPath: string,
): Promise<InferredInterface[]> {
  const modules = parseMavenModules(pomPath)
  const pomDir = join(pomPath, '..') // dirname

  if (modules.length === 0) return []

  const results: InferredInterface[] = []

  for (const mod of modules) {
    const modDir = join(pomDir, mod)
    const javaSrcDir = join(modDir, 'src', 'main', 'java')
    if (!existsSync(javaSrcDir)) continue

    const javaFiles = collectJavaFiles(javaSrcDir)
    if (javaFiles.length === 0) continue

    const exports = extractJavaSymbolsFromFiles(javaFiles)
    if (exports.length === 0) continue

    results.push({
      module: mod,
      source_glob: `${mod}/src/main/java/**/*.java`,
      exports: [...new Set(exports)].sort(),
    })
  }

  return results.sort((a, b) => a.module.localeCompare(b.module))
}

/**
 * Collect .java files under a single source root (non-recursive into sub-modules,
 * since Maven modules are already split at the pom level).
 */
function collectJavaFiles(srcDir: string): string[] {
  const out: string[] = []
  const walk = (dir: string) => {
    let entries: string[]
    try {
      entries = readdirSync(dir)
    } catch {
      return
    }
    for (const name of entries) {
      const full = join(dir, name)
      let st
      try {
        st = statSync(full)
      } catch {
        continue
      }
      if (st.isDirectory()) {
        walk(full)
      } else if (st.isFile() && full.endsWith('.java')) {
        out.push(full)
      }
    }
  }
  walk(srcDir)
  return out
}

/**
 * Recursive .java scan from project root (fallback when no pom.xml or no <modules>).
 */
function collectJavaFilesRecursive(projectRoot: string): string[] {
  const out: string[] = []
  const walk = (dir: string) => {
    let entries: string[]
    try {
      entries = readdirSync(dir)
    } catch {
      return
    }
    for (const name of entries) {
      if (name === 'node_modules' || name === '.git' || name === 'target') continue
      const full = join(dir, name)
      let st
      try {
        st = statSync(full)
      } catch {
        continue
      }
      if (st.isDirectory()) {
        walk(full)
      } else if (st.isFile() && full.endsWith('.java')) {
        out.push(full)
      }
    }
  }
  walk(projectRoot)
  return out
}

/**
 * Extract Java public type declarations (interface, class, enum, record)
 * from a list of .java file paths. Uses regex-based extraction — no full
 * Java parser dependency.
 *
 * Matches patterns like:
 *   public interface Foo
 *   public class Foo
 *   public abstract class Foo
 *   public enum Foo
 *   public record Foo
 *
 * Skips non-public types (package-private, protected, private).
 */
function extractJavaSymbolsFromFiles(filePaths: string[]): string[] {
  const symbols = new Set<string>()

  for (const filePath of filePaths) {
    let content: string
    try {
      content = readFileSync(filePath, 'utf-8')
    } catch {
      continue
    }
    if (!content.trim()) continue

    // Match public (abstract)? (interface|class|enum|record) Name
    // Using a simple regex — covers 95%+ of real-world Java declarations
    const re = /public\s+(?:abstract\s+)?(interface|class|enum|record)\s+(\w+)/g
    let match: RegExpExecArray | null
    while ((match = re.exec(content)) !== null) {
      // Skip inner classes (preceded by a type name on the same line before "class")
      // and annotation declarations (@interface)
      symbols.add(match[2])
    }
  }

  return [...symbols].sort()
}

/**
 * Build graph from a flat collection of .java files (no Maven module structure).
 * Derives module names from the relative path: src/main/java → parent dir.
 */
async function inferJavaGraphFromFiles(
  projectRoot: string,
  javaFiles: string[],
): Promise<InferredInterface[]> {
  const byModule = new Map<string, { exports: Set<string>; sourceRoot: string }>()

  for (const absPath of javaFiles) {
    const rel = relative(projectRoot, absPath).replace(/\\/g, '/')
    const moduleName = deriveJavaModuleName(rel)
    const record = byModule.get(moduleName)

    let content: string
    try {
      content = readFileSync(absPath, 'utf-8')
    } catch {
      continue
    }
    if (!content.trim()) continue

    const re = /public\s+(?:abstract\s+)?(interface|class|enum|record)\s+(\w+)/g
    let match: RegExpExecArray | null
    const names: string[] = []
    while ((match = re.exec(content)) !== null) {
      names.push(match[2])
    }

    if (record) {
      for (const n of names) record.exports.add(n)
    } else {
      byModule.set(moduleName, {
        exports: new Set(names),
        sourceRoot: deriveJavaSourceGlob(rel),
      })
    }
  }

  const results: InferredInterface[] = []
  for (const [mod, rec] of byModule) {
    if (rec.exports.size === 0) continue
    results.push({
      module: mod,
      source_glob: rec.sourceRoot,
      exports: [...rec.exports].sort(),
    })
  }

  return results.sort((a, b) => a.module.localeCompare(b.module))
}

/**
 * Derive a module name from a relative Java file path.
 * For Maven: agent-bus/src/main/java/com/example/... → module = agent-bus
 * For flat: src/main/java/com/example/... → module = src (fallback)
 */
function deriveJavaModuleName(relPath: string): string {
  const parts = relPath.split('/')
  // Maven style: {module}/src/main/java/...
  const mainIdx = parts.indexOf('main')
  if (mainIdx >= 2 && parts[mainIdx - 1] === 'src') {
    // parts[0..mainIdx-2] is the module path
    return parts.slice(0, mainIdx - 1).join('/')
  }
  // Flat: src/main/java/... or direct java files
  const srcIdx = parts.indexOf('src')
  if (srcIdx >= 0 && srcIdx < parts.length - 1) {
    return parts.slice(0, srcIdx).join('/') || 'root'
  }
  return 'root'
}

function deriveJavaSourceGlob(relPath: string): string {
  const parts = relPath.split('/')
  const mainIdx = parts.indexOf('main')
  if (mainIdx >= 2 && parts[mainIdx - 1] === 'src') {
    const modPath = parts.slice(0, mainIdx - 1).join('/')
    return `${modPath}/src/main/java/**/*.java`
  }
  return '**/*.java'
}

// ─── Shared helpers ────────────────────────────────────────────────────

function deriveModuleName(relPath: string): string {
  const parts = relPath.split('/')
  if (parts.length >= 2 && parts[0] === 'src') {
    return parts[1]
  }
  return 'root'
}
